import { Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { DateTime } from 'luxon';

import { EventTypesProvider } from '../../../event';
import {
    CreateJobAdditionalInformation,
    CreateJobContact,
    CreateJobEventInformation,
    CreateJobGuards,
    CreateJobOngoingDetails,
    CreateJobUniform,
    Job,
    JobDate
} from '../../Domain/Model/CreateJob/create-job.model';
import { WebformCreateJobFlowState } from '../../Domain/Port/CreateJob/webform-create-job-flow.state';

@Injectable({
    providedIn: 'root'
})
export class CreateJobModelFactory {
    constructor(
        private readonly eventTypesProvider: EventTypesProvider,
        private readonly jobFlowState: WebformCreateJobFlowState
    ) {}

    /**
     * Creates a CreateJob instance from CreateJobService data
     * @returns A new CreateJob instance
     */
    async createFromService(): Promise<Job> {
        // Create date object
        const date = await this.createJobDate(this.jobFlowState);

        // Create guards object
        const guards = await this.createJobGuards(this.jobFlowState);

        // Create contact object
        const contact = await this.createContact(this.jobFlowState);

        // Create event information object
        const eventInformation = await this.createEventInformation(this.jobFlowState);

        // Create additional information object
        const additionalInformation = await this.createAdditionalInformation(this.jobFlowState);

        // Create uniform object if required
        const requireUniform = await this.jobFlowState.requireUniform();
        const uniform = requireUniform ? await this.createUniform(this.jobFlowState) : undefined;

        // Create repeat job object if applicable
        const jobType = await this.jobFlowState.jobType();
        const repeatJob = jobType === 'Ongoing' ? await this.createRepeatJob(this.jobFlowState) : undefined;

        // Return the complete CreateJob instance
        return new Job(jobType, date, guards, contact, eventInformation, additionalInformation, uniform, repeatJob);
    }

    private async createJobDate(service: WebformCreateJobFlowState): Promise<JobDate> {
        const startDate = await this.createStartDate(await service.startDate(), await service.startTime());
        return new JobDate(startDate);
    }

    private async createStartDate(date: string | null, time: string | null): Promise<DateTime> {
        // Validate inputs
        if (!date) {
            throw new Error('Start date is not set in the service');
        }
        if (!time) {
            throw new Error('Start time is not set in the service');
        }

        return this.createDate(date, time);
    }

    private async createDate(date: string, time?: string): Promise<DateTime> {
        if (!date) {
            throw new Error('Date is not set in the service');
        }
        if (!time) {
            time = '23:59:59';
        }

        // Combine date and time into an ISO 8601 string (e.g., "2025-05-29T14:30:00")
        const dateTimeStr = `${date}T${time}`;

        // Create a Luxon DateTime object from the string, interpreted as local time
        const dateTime = DateTime.fromISO(dateTimeStr);

        // Check if the DateTime object is valid
        if (!dateTime.isValid) {
            throw new Error(`Start date has invalid format: ${dateTimeStr}, error: ${dateTime.invalidReason}`);
        }

        return dateTime;
    }

    private async createJobGuards(service: WebformCreateJobFlowState) {
        const guardsCount = await service.numberOfGuards();
        if (!guardsCount) {
            throw new Error('Number of guards is not set in the service');
        }

        const hoursGuardPerDay = await service.numberOfHoursPerDay();
        if (!hoursGuardPerDay) {
            throw new Error('Hours per day is not set in the service');
        }

        // Using the class constructor to ensure validation
        return new CreateJobGuards(guardsCount, hoursGuardPerDay);
    }

    private async createContact(service: WebformCreateJobFlowState) {
        const name = await service.primaryContactName();
        if (!name) {
            throw new Error('Primary contact name is not set in the service');
        }
        const phone = await service.primaryContactPhone();
        if (!phone) {
            throw new Error('Primary contact phone is not set in the service');
        }
        const email = await service.primaryContactEmail();
        if (!email) {
            throw new Error('Primary contact email is not set in the service');
        }

        // Using the class constructor to ensure validation
        return new CreateJobContact(name, phone, email);
    }

    private async createEventInformation(service: WebformCreateJobFlowState): Promise<CreateJobEventInformation> {
        const availableEvents = await firstValueFrom(this.eventTypesProvider.getEvents());
        if (availableEvents.isError()) {
            throw new Error('Failed to fetch event types');
        }

        // Find the event type object from the options
        const eventType = await service.eventType();
        if (!eventType) {
            throw new Error('Event type is not set in the service');
        }

        const eventTypeObj = availableEvents.getResult().find(e => e.value === eventType);
        if (!eventTypeObj) {
            throw new Error('Event type not found');
        }

        const eventLocationAddress = await service.eventLocationAddress();
        if (!eventLocationAddress) {
            throw new Error('Event location address is not set in the service');
        }

        // Using the class constructor to ensure validation
        return new CreateJobEventInformation(eventTypeObj, eventLocationAddress);
    }

    private async createAdditionalInformation(
        service: WebformCreateJobFlowState
    ): Promise<CreateJobAdditionalInformation> {
        const jobDescription = await service.jobDescription();
        if (!jobDescription) {
            throw new Error('Job description is not set in the service');
        }
        const previousIssues = (await service.previousSecurityIssues()) || undefined;
        const emergencyProcedures = (await service.emergencyProcedures()) || undefined;

        const guardPrimaryDuties = (await service.guardPrimaryDuties()) || undefined;

        // Using the class constructor to ensure validation
        return new CreateJobAdditionalInformation(
            jobDescription,
            previousIssues,
            emergencyProcedures,
            guardPrimaryDuties
        );
    }

    private async createUniform(service: WebformCreateJobFlowState) {
        const requireUniform = await service.requireUniform();
        if (!requireUniform) {
            return undefined;
        }

        const uniformType = await service.uniformType();
        if (!uniformType) {
            throw new Error('Uniform type is not set in the service');
        }

        const providedBy = await service.uniformProvidedBy();
        if (!providedBy) {
            throw new Error('Uniform provided by is not set in the service');
        }

        // Using the class constructor to ensure validation
        return new CreateJobUniform(uniformType, providedBy);
    }

    private async createRepeatJob(service: WebformCreateJobFlowState) {
        const jobType = await service.jobType();
        if (jobType !== 'Ongoing') {
            return undefined;
        }

        const endDateInService = await service.endDate();
        const startTimeInService = await service.startTime();
        const endDate = endDateInService
            ? await this.createDate(endDateInService, startTimeInService || undefined)
            : undefined;
        if (!endDate || !endDate.isValid) {
            throw new Error('ongoingJobEndDate is not set in the service');
        }
        const repeatCycle = (await service.repeatCycle()) || undefined;
        const repeatCycleCustomType = (await service.repeatCycleCustomType()) || '';

        const repeatCycleFrequencyAmount = await service.repeatCycleFrequencyAmount();
        if (repeatCycle === 'custom' && (!repeatCycleFrequencyAmount || repeatCycleFrequencyAmount <= 0)) {
            throw new Error('Repeat cycle frequency amount is not set in the service');
        }

        const repeatDays = (await service.repeatDays()) || [];

        const repeatEndDateStr = await service.repeatEndDate();
        const repeatEndDate = repeatEndDateStr ? DateTime.fromISO(repeatEndDateStr) : undefined;

        // Using the class constructor to ensure validation
        return new CreateJobOngoingDetails(
            endDate,
            repeatCycle,
            repeatCycleCustomType,
            repeatCycleFrequencyAmount || 0,
            repeatDays,
            repeatEndDate
        );
    }
}
